<template>
  <DashboardLayout>
    <template #header>
      <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Billing & Payment Methods
      </h1>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Manage your payment methods and view billing history
      </p>
    </template>

    <div class="max-w-5xl mx-auto space-y-6">
      <!-- Current Subscription Summary -->
      <Card>
        <template #header>
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Current Subscription
            </h2>
            <Badge :variant="subscriptionStatusVariant">
              {{ subscription.status }}
            </Badge>
          </div>
        </template>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p class="text-sm text-gray-600 dark:text-gray-400">Plan</p>
            <p class="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ subscription.plan.name }}
            </p>
          </div>
          <div>
            <p class="text-sm text-gray-600 dark:text-gray-400">Amount</p>
            <p class="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ formatCurrency(subscription.amount) }} / {{ subscription.billing_period }}
            </p>
          </div>
          <div>
            <p class="text-sm text-gray-600 dark:text-gray-400">Next Billing Date</p>
            <p class="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ formatDate(subscription.next_billing_date) }}
            </p>
          </div>
        </div>
      </Card>

      <!-- Payment Methods -->
      <Card>
        <template #header>
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Payment Methods
            </h2>
            <Button @click="showAddPaymentMethodModal = true" size="sm">
              <svg class="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Payment Method
            </Button>
          </div>
        </template>

        <div v-if="paymentMethods.length === 0" class="text-center py-12">
          <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No payment methods
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Add a payment method to manage your subscription
          </p>
          <Button @click="showAddPaymentMethodModal = true">
            Add Payment Method
          </Button>
        </div>

        <div v-else class="space-y-4">
          <div
            v-for="method in paymentMethods"
            :key="method.id"
            class="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div class="flex items-center space-x-4">
              <!-- Card Icon -->
              <div class="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <svg class="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                </svg>
              </div>

              <!-- Card Info -->
              <div>
                <div class="flex items-center space-x-2">
                  <p class="font-medium text-gray-900 dark:text-gray-100">
                    {{ method.brand }} •••• {{ method.last4 }}
                  </p>
                  <Badge v-if="method.is_default" variant="primary" size="xs">
                    Default
                  </Badge>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Expires {{ method.exp_month }}/{{ method.exp_year }}
                </p>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center space-x-2">
              <Button
                v-if="!method.is_default"
                @click="setDefaultPaymentMethod(method)"
                variant="secondary"
                size="sm"
              >
                Set as Default
              </Button>
              <Button
                @click="confirmRemovePaymentMethod(method)"
                variant="danger"
                size="sm"
                :disabled="method.is_default && paymentMethods.length === 1"
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <!-- Billing History -->
      <Card>
        <template #header>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Billing History
          </h2>
        </template>

        <div v-if="invoices.length === 0" class="text-center py-12">
          <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No invoices yet
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Your billing history will appear here
          </p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200 dark:border-gray-700">
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Invoice
                </th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Date
                </th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Amount
                </th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Status
                </th>
                <th class="px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="invoice in invoices"
                :key="invoice.id"
                class="border-b border-gray-200 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                  #{{ invoice.number }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                  {{ formatDate(invoice.date) }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                  {{ formatCurrency(invoice.amount) }}
                </td>
                <td class="px-4 py-3">
                  <Badge :variant="getInvoiceStatusVariant(invoice.status)" size="xs">
                    {{ invoice.status }}
                  </Badge>
                </td>
                <td class="px-4 py-3 text-right">
                  <Button
                    @click="downloadInvoice(invoice)"
                    variant="secondary"
                    size="sm"
                  >
                    <svg class="w-4 h-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    Download
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>

    <!-- Add Payment Method Modal -->
    <Modal :show="showAddPaymentMethodModal" @close="closeAddPaymentMethodModal">
      <template #header>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Add Payment Method
        </h3>
      </template>

      <template #content>
        <form @submit.prevent="addPaymentMethod" class="space-y-4">
          <!-- Card Number -->
          <Input
            v-model="paymentMethodForm.card_number"
            label="Card Number"
            type="text"
            placeholder="1234 5678 9012 3456"
            maxlength="19"
            required
            :error="paymentMethodForm.errors.card_number"
          />

          <div class="grid grid-cols-2 gap-4">
            <!-- Expiry Date -->
            <Input
              v-model="paymentMethodForm.expiry_date"
              label="Expiry Date"
              type="text"
              placeholder="MM/YY"
              maxlength="5"
              required
              :error="paymentMethodForm.errors.expiry_date"
            />

            <!-- CVC -->
            <Input
              v-model="paymentMethodForm.cvc"
              label="CVC"
              type="text"
              placeholder="123"
              maxlength="4"
              required
              :error="paymentMethodForm.errors.cvc"
            />
          </div>

          <!-- Cardholder Name -->
          <Input
            v-model="paymentMethodForm.cardholder_name"
            label="Cardholder Name"
            type="text"
            placeholder="John Doe"
            required
            :error="paymentMethodForm.errors.cardholder_name"
          />

          <!-- Set as Default -->
          <div class="flex items-center">
            <input
              id="set-default"
              v-model="paymentMethodForm.set_as_default"
              type="checkbox"
              class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label for="set-default" class="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Set as default payment method
            </label>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              @click="closeAddPaymentMethodModal"
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              :disabled="paymentMethodForm.processing"
            >
              {{ paymentMethodForm.processing ? 'Adding...' : 'Add Payment Method' }}
            </Button>
          </div>
        </form>
      </template>
    </Modal>

    <!-- Confirm Remove Payment Method Modal -->
    <ConfirmModal
      :show="showConfirmRemoveModal"
      title="Remove Payment Method?"
      :message="`Are you sure you want to remove ${paymentMethodToRemove?.brand} •••• ${paymentMethodToRemove?.last4}?`"
      confirm-text="Remove"
      confirm-variant="danger"
      :processing="removing"
      @confirm="handleRemovePaymentMethod"
      @cancel="cancelRemovePaymentMethod"
    />
  </DashboardLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Badge from '@/Components/Common/Badge.vue';
import Button from '@/Components/Common/Button.vue';
import Input from '@/Components/Common/Input.vue';
import Modal from '@/Components/Common/Modal.vue';
import ConfirmModal from '@/Components/Common/ConfirmModal.vue';

const props = defineProps({
  subscription: {
    type: Object,
    required: true,
  },
  paymentMethods: {
    type: Array,
    default: () => [],
  },
  invoices: {
    type: Array,
    default: () => [],
  },
});

const showAddPaymentMethodModal = ref(false);
const showConfirmRemoveModal = ref(false);
const paymentMethodToRemove = ref(null);
const removing = ref(false);

/**
 * Payment method form
 */
const paymentMethodForm = useForm({
  card_number: '',
  expiry_date: '',
  cvc: '',
  cardholder_name: '',
  set_as_default: props.paymentMethods.length === 0,
});

/**
 * Get badge variant for subscription status
 */
const subscriptionStatusVariant = computed(() => {
  const status = props.subscription.status;

  switch (status) {
    case 'active':
      return 'success';
    case 'trialing':
      return 'info';
    case 'past_due':
      return 'warning';
    case 'canceled':
    case 'suspended':
      return 'danger';
    default:
      return 'secondary';
  }
});

/**
 * Get badge variant for invoice status
 */
const getInvoiceStatusVariant = (status) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'secondary';
  }
};

/**
 * Format currency
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: props.subscription.currency || 'USD',
  }).format(amount);
};

/**
 * Format date
 */
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Add payment method
 */
const addPaymentMethod = () => {
  paymentMethodForm.post(route('settings.billing.payment-method.add'), {
    preserveScroll: true,
    onSuccess: () => {
      closeAddPaymentMethodModal();
    },
  });
};

/**
 * Close add payment method modal
 */
const closeAddPaymentMethodModal = () => {
  showAddPaymentMethodModal.value = false;
  paymentMethodForm.reset();
  paymentMethodForm.clearErrors();
};

/**
 * Set default payment method
 */
const setDefaultPaymentMethod = (method) => {
  router.post(
    route('settings.billing.payment-method.set-default', method.id),
    {},
    {
      preserveScroll: true,
    }
  );
};

/**
 * Confirm remove payment method
 */
const confirmRemovePaymentMethod = (method) => {
  paymentMethodToRemove.value = method;
  showConfirmRemoveModal.value = true;
};

/**
 * Handle remove payment method
 */
const handleRemovePaymentMethod = () => {
  if (!paymentMethodToRemove.value) return;

  removing.value = true;

  router.delete(route('settings.billing.payment-method.remove', paymentMethodToRemove.value.id), {
    preserveScroll: true,
    onSuccess: () => {
      showConfirmRemoveModal.value = false;
      paymentMethodToRemove.value = null;
      removing.value = false;
    },
    onError: () => {
      removing.value = false;
    },
  });
};

/**
 * Cancel remove payment method
 */
const cancelRemovePaymentMethod = () => {
  showConfirmRemoveModal.value = false;
  paymentMethodToRemove.value = null;
};

/**
 * Download invoice
 */
const downloadInvoice = (invoice) => {
  window.open(route('invoices.download', invoice.id), '_blank');
};
</script>
